import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {message} from 'antd';
import {recycleService} from '@/services/recycleService';
import type {
  AddTrackingNumberCommand,
  CreateReturnLogisticCommand,
  LogisticTrackingDTO,
  RecycleLogistic,
  RecycleLogisticQuery
} from '@/types/recycle';

// 异步操作
export const fetchLogistics = createAsyncThunk(
  'recycleLogistic/fetchLogistics',
  async (query: RecycleLogisticQuery) => {
    const response = await recycleService.getLogistics(query);
    return response;
  }
);

export const fetchOrderLogistics = createAsyncThunk(
  'recycleLogistic/fetchOrderLogistics',
  async (orderId: number) => {
    const response = await recycleService.getOrderLogistics(orderId);
    return response;
  }
);

export const updateLogisticStatus = createAsyncThunk(
  'recycleLogistic/updateLogisticStatus',
  async ({ id, status }: { id: number; status: string }) => {
    const response = await recycleService.updateLogisticStatus(id, status);
    return response;
  }
);

export const addTrackingNumber = createAsyncThunk(
  'recycleLogistic/addTrackingNumber',
  async (data: AddTrackingNumberCommand) => {
    const response = await recycleService.addTrackingNumber(data);
    return response;
  }
);

export const createReturnLogistic = createAsyncThunk(
  'recycleLogistic/createReturnLogistic',
  async (data: CreateReturnLogisticCommand) => {
    const response = await recycleService.createReturnLogistic(data);
    return response;
  }
);

export const fetchLogisticTracking = createAsyncThunk(
  'recycleLogistic/fetchLogisticTracking',
  async (logisticId: number) => {
    const response = await recycleService.getLogisticTracking(logisticId);
    return response;
  }
);

// 状态类型
interface RecycleLogisticState {
  list: RecycleLogistic[];
  orderLogistics: RecycleLogistic[];
  currentTracking: LogisticTrackingDTO | null;
  total: number;
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: RecycleLogisticState = {
  list: [],
  orderLogistics: [],
  currentTracking: null,
  total: 0,
  loading: false,
  error: null,
};

// Slice
const recycleLogisticSlice = createSlice({
  name: 'recycleLogistic',
  initialState,
  reducers: {
    clearOrderLogistics: (state) => {
      state.orderLogistics = [];
    },
    clearCurrentTracking: (state) => {
      state.currentTracking = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchLogistics
    builder
      .addCase(fetchLogistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogistics.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchLogistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取物流列表失败';
        message.error(state.error);
      });

    // fetchOrderLogistics
    builder
      .addCase(fetchOrderLogistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderLogistics.fulfilled, (state, action) => {
        state.loading = false;
        state.orderLogistics = action.payload;
      })
      .addCase(fetchOrderLogistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取订单物流失败';
        message.error(state.error);
      });

    // updateLogisticStatus
    builder
      .addCase(updateLogisticStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLogisticStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        const orderIndex = state.orderLogistics.findIndex(item => item.id === action.payload.id);
        if (orderIndex !== -1) {
          state.orderLogistics[orderIndex] = action.payload;
        }
        message.success('更新物流状态成功');
      })
      .addCase(updateLogisticStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新物流状态失败';
        message.error(state.error);
      });

    // addTrackingNumber
    builder
      .addCase(addTrackingNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTrackingNumber.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        const orderIndex = state.orderLogistics.findIndex(item => item.id === action.payload.id);
        if (orderIndex !== -1) {
          state.orderLogistics[orderIndex] = action.payload;
        }
        message.success('添加物流单号成功');
      })
      .addCase(addTrackingNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '添加物流单号失败';
        message.error(state.error);
      });

    // createReturnLogistic
    builder
      .addCase(createReturnLogistic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReturnLogistic.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        const orderIndex = state.orderLogistics.findIndex(item => item.id === action.payload.id);
        if (orderIndex !== -1) {
          state.orderLogistics[orderIndex] = action.payload;
        }
        message.success('创建寄回物流记录成功');
      })
      .addCase(createReturnLogistic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建寄回物流记录失败';
        message.error(state.error);
      });

    // fetchLogisticTracking
    builder
      .addCase(fetchLogisticTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogisticTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTracking = action.payload;
      })
      .addCase(fetchLogisticTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取物流跟踪失败';
        message.error(state.error);
      });
  },
});

export const { clearOrderLogistics, clearCurrentTracking, clearError } = recycleLogisticSlice.actions;
export default recycleLogisticSlice.reducer;
